import React, { FC, useCallback } from 'react';
import { Area, Bar, Line, YAxis } from 'recharts';
import { beautifyEnum } from 'src/utils/utility';
import { safeFormatDateString } from 'src/utils/date';
import IconChartLine from 'src/presentations/components/atom/icons/ChartLine';
import IconChartBar from 'src/presentations/components/atom/icons/ChartBar';
import { ChipChart } from 'src/presentations/components/atom/chip/ChipChart';
import { ICardAnalyticItemProps } from 'src/presentations/components/molecules/card/cardAnalytic/types';
import { ChartViewType, ComponentOptionDto } from '../sidebar/types';
import { colorMapper, formatNumberChart } from '../analyticsMapper';
import { sortAggregatedData } from '../useAnalytics';
import { useAnalyticsData } from '../context/AnalyticsDataContext';
import MyComposedChart from './CustomComposedChart';

interface AnalyticsChartProps {
  selectedCards: ICardAnalyticItemProps[];
  selectedChartView: ChartViewType;
  formValues: any;
  updateFiltersOnChartClick: (payload: any) => void;
}

export const AnalyticsChart: FC<AnalyticsChartProps> = ({ selectedCards, selectedChartView, formValues, updateFiltersOnChartClick }) => {
  const { chartData } = useAnalyticsData();

  // Helper function to convert title (string or ReactNode) to plain string for chart
  const getTitleAsString = (title: string | React.ReactNode): string => {
    if (typeof title === 'string') {
      return title;
    }
    // For React elements like <CO2 /> Emissions, return the plain text equivalent
    return 'CO2e Emissions';
  };

  const ListMetrics = useCallback(
    ({ options }) => (
      <div className='flex items-center gap-8 flex-wrap'>
        {options.slice(0, 3).map((metric) => (
          <ChipChart
            key={`chip-metric-${metric?.name}`}
            label={metric?.name}
            icon={
              <div className='w-18 xl:w-22 h-18 xl:h-20 rounded-5 flex items-center justify-center' style={{ backgroundColor: metric?.color }}>
                {metric?.type === 'line' ? <IconChartLine color='#ffffff' /> : <IconChartBar color='#ffffff' />}
              </div>
            }
          />
        ))}
        {options.length > 3 && <span className='text-12 font-medium text-neutral-60'>...</span>}
      </div>
    ),
    []
  );

  const metricOptions = selectedCards?.map(
    (v, i): ComponentOptionDto => ({
      dataKey: v.id,
      color: colorMapper(v.theme),
      name: getTitleAsString(v.title),
      type: v.chartType,
      unit: v.unit,
      orientation: new Set(selectedCards.slice(0, i).map((v) => v.unit)).size > 1 ? 'right' : 'left',
      mirror: new Set(selectedCards.slice(0, i).map((v) => v.unit)).size === 1,
    })
  );

  const tooltipTitle = selectedChartView === ChartViewType.TREND ? `Time` : beautifyEnum(formValues?.granularity);

  return (
    <div className='flex-1 flex flex-col'>
      <div className='flex-1 overflow-hidden'>
        <MyComposedChart
          data={selectedChartView !== ChartViewType.TREND ? sortAggregatedData(chartData?.aggregated, selectedCards?.map((v) => v.id)) : chartData?.aggregated}
          xAxisKey='aggregatedUnit'
          formatter={(() => {
            if (formValues?.granularity === 'DAILY') {
              return safeFormatDateString;
            }
            if (formValues?.granularity === 'SERVICE_PROVIDER_CODE') {
              return beautifyEnum;
            }
            return undefined;
          })()}
          tooltipTitle={tooltipTitle}
        >
          {metricOptions
            .slice()
            ?.sort((a, b) => {
              if (a.type === b.type) {
                return 0;
              }
              if (a.type === 'bar') {
                return -1;
              }
              if (b.type === 'bar') {
                return 1;
              }
              return 0;
            })
            .map((option) => {
              const mainComponent = () => {
                switch (option.type) {
                  case 'line':
                    return (
                      <Line
                        connectNulls
                        name={option.name}
                        yAxisId={option.unit}
                        dataKey={option.dataKey}
                        type='linear'
                        stroke={option.color}
                        strokeWidth={3}
                        dot={false}
                        isAnimationActive={false}
                        style={{
                          filter: `drop-shadow(0px 2px 7px rgba(0, 0, 0, 0.25))`,
                        }}
                        activeDot={{ onClick: (props, event) => updateFiltersOnChartClick((event as any).payload) }}
                      />
                    );
                  case 'area':
                    return (
                      <Area
                        connectNulls
                        name={option.name}
                        yAxisId={option.unit}
                        dataKey={option.dataKey}
                        stroke={option.color}
                        fill={option.color}
                        isAnimationActive={false}
                        activeDot={{ onClick: (props, event) => updateFiltersOnChartClick((event as any).payload) }}
                      />
                    );
                  case 'bar':
                  default:
                    return (
                      <Bar
                        unit={option.unit}
                        yAxisId={option.unit}
                        dataKey={option.dataKey}
                        barSize={40}
                        fill={option.color}
                        radius={[5, 5, 0, 0]}
                        isAnimationActive={false}
                        onClick={(event) => updateFiltersOnChartClick(event.payload)}
                      />
                    );
                }
              };

              return (
                <React.Fragment key={option.dataKey}>
                  {mainComponent()}
                  <YAxis
                    name={option.name}
                    label={{ value: option.unit, angle: 0, position: 'top', offset: 20, fontSize: 12 }}
                    yAxisId={option.unit}
                    orientation={option.orientation}
                    mirror={option.mirror}
                    tickFormatter={(value) => formatNumberChart(value)}
                    tick={{ fontSize: 12 }}
                  />
                </React.Fragment>
              );
            })}
        </MyComposedChart>
      </div>

      <div className='w-full flex items-center gap-10 xl:gap-14 px-16 xl:px-24 py-8 xl:py-10 bg-neutral-10 border-t border-neutral-20 rounded-b-16'>
        <div className='flex items-center gap-6 xl:gap-8 text-12 font-medium text-neutral-90'>
          <span className='capitalize text-neutral-60'>
            {selectedChartView === ChartViewType.TREND ? `${beautifyEnum(formValues?.granularity)} Trend of` : 'Distribution of'}
          </span>
          <ListMetrics options={metricOptions} />
          {selectedChartView !== ChartViewType.TREND && <span className='capitalize text-neutral-60'>by {beautifyEnum(formValues?.granularity)}</span>}
          <div className='flex items-center gap-8 text-neutral-60'>
            from
            <span>{chartData?.dateRange?.from}</span>
            to
            <span>{chartData?.dateRange?.to}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
